import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  DollarSign, Loader2, TrendingUp, AlertTriangle, 
  Clock, CheckCircle2, Send, RefreshCw, Calendar, User
} from 'lucide-react';
import usePlatformFee from '@/hooks/usePlatformFee';

const FINANCIAL_STATUS_CONFIG = {
  disponivel: { label: 'Disponível', color: 'bg-emerald-100 text-emerald-700' },
  pago: { label: 'Pago', color: 'bg-blue-100 text-blue-700' },
  em_analise: { label: 'Em Análise', color: 'bg-orange-100 text-orange-700' },
  em_retencao: { label: 'Em Retenção', color: 'bg-yellow-100 text-yellow-700' },
  bloqueado: { label: 'Bloqueado', color: 'bg-red-100 text-red-700' },
  revertido: { label: 'Valor revertido', color: 'bg-red-50 text-red-700' }
};

export default function ProviderFinancial() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // SOURCE OF TRUTH dinâmico para comissão (admin → Financeiro → Configurações)
  const { providerNetRatio, platformFeePercent } = usePlatformFee();
  const [financialSummary, setFinancialSummary] = useState(null);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Refresh automático a cada 30 segundos
  const REFRESH_INTERVAL = 30000;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/provider/login');
      return;
    }
    if (!user) return;
    if (user.user_type !== 'provider') {
      navigate('/provider/login');
      return;
    }
    
    fetchData();
    
    // Configurar refresh automático
    const interval = setInterval(() => {
      fetchData(true); // silent = true para não mostrar loading
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [user, navigate]);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [summaryRes, payoutRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/provider/financial-summary`, { headers }),
        axios.get(`${API}/provider/payout-status`, { headers }),
        axios.get(`${API}/provider/appointments?status=COMPLETED`, { headers }).catch(() => ({ data: { appointments: [] } }))
      ]);
      
      setFinancialSummary(summaryRes.data);
      setPayoutStatus(payoutRes.data);
      setTransactions(transactionsRes.data?.appointments || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
      if (!silent) toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!window.confirm(`Deseja solicitar o repasse de ${formatCurrency(payoutStatus?.available_balance)}?`)) {
      return;
    }
    
    setRequestingPayout(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/request-payout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Solicitação de repasse enviada com sucesso!');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao solicitar repasse';
      toast.error(detail);
    } finally {
      setRequestingPayout(false);
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toFixed(2).replace('.', ',')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatServiceType = (type) => {
    if (!type) return 'Lavagem';
    const lower = type.toLowerCase();
    if (lower.includes('completa') || lower === 'complete') return 'Lavagem Completa';
    if (lower.includes('ducha') || lower === 'ducha') return 'Ducha Simples';
    return type;
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-40" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold" data-testid="financial-title">Financeiro</h1>
              <p className="text-sm text-slate-500">
                Resumo e extrato financeiro
                {lastUpdate && (
                  <span className="ml-2 text-xs text-slate-400">
                    • Atualizado: {lastUpdate.toLocaleTimeString('pt-BR')}
                  </span>
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchData()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {/* Cards Financeiros */}
          {financialSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              {/* Faturamento Bruto */}
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">Faturamento</span>
                </div>
                <p className="text-lg font-bold text-blue-800">
                  {formatCurrency(financialSummary.faturamento_bruto)}
                </p>
              </Card>

              {/* Pago */}
              <Card className="p-4 bg-slate-50 border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-slate-600" />
                  <span className="text-xs font-medium text-slate-700">Pago</span>
                </div>
                <p className="text-lg font-bold text-slate-700">
                  {formatCurrency(financialSummary.pago)}
                </p>
              </Card>

              {/* Em Análise */}
              <Card className={`p-4 ${financialSummary.em_analise > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={`w-4 h-4 ${financialSummary.em_analise > 0 ? 'text-orange-600' : 'text-slate-500'}`} />
                  <span className={`text-xs font-medium ${financialSummary.em_analise > 0 ? 'text-orange-700' : 'text-slate-600'}`}>Em Análise</span>
                </div>
                <p className={`text-lg font-bold ${financialSummary.em_analise > 0 ? 'text-orange-700' : 'text-slate-500'}`}>
                  {formatCurrency(financialSummary.em_analise)}
                </p>
              </Card>

              {/* Em Retenção */}
              <Card className={`p-4 ${financialSummary.em_retencao > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className={`w-4 h-4 ${financialSummary.em_retencao > 0 ? 'text-yellow-600' : 'text-slate-500'}`} />
                  <span className={`text-xs font-medium ${financialSummary.em_retencao > 0 ? 'text-yellow-700' : 'text-slate-600'}`}>Em Retenção</span>
                </div>
                <p className={`text-lg font-bold ${financialSummary.em_retencao > 0 ? 'text-yellow-700' : 'text-slate-500'}`}>
                  {formatCurrency(financialSummary.em_retencao)}
                </p>
              </Card>

              {/* Disponível */}
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-medium text-green-700">Disponível</span>
                </div>
                <p className="text-lg font-bold text-green-700">
                  {formatCurrency(financialSummary.disponivel)}
                </p>
              </Card>
            </div>
          )}

          {/* Bloco Repasse */}
          {payoutStatus && payoutStatus.mode === 'manual' && (
            <Card className="p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Solicitar Repasse
              </h2>
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm text-slate-500 mb-1">Saldo disponível para repasse</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="available-balance">
                    {formatCurrency(payoutStatus.available_balance)}
                  </p>
                  
                  {payoutStatus.pending_request && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-700">
                        <strong>Solicitação pendente:</strong> {formatCurrency(payoutStatus.pending_request.amount)}
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Enviada em: {formatDate(payoutStatus.pending_request.created_at)}
                      </p>
                    </div>
                  )}
                  
                  {payoutStatus.has_open_disputes && payoutStatus.block_on_dispute && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-700">
                        Não é possível solicitar repasse com contestações em aberto
                      </p>
                    </div>
                  )}
                </div>
                
                <Button 
                  size="lg"
                  onClick={handleRequestPayout}
                  disabled={!payoutStatus.can_request || requestingPayout}
                  data-testid="request-payout-btn"
                  className="min-w-[200px]"
                >
                  {requestingPayout ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  {requestingPayout ? 'Enviando...' : 'Solicitar Repasse'}
                </Button>
              </div>
            </Card>
          )}

          {/* Bloco Extrato */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Extrato de Serviços
            </h2>
            
            {transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-slate-600">Data</th>
                      <th className="pb-3 font-medium text-slate-600">Cliente</th>
                      <th className="pb-3 font-medium text-slate-600">Serviço</th>
                      <th className="pb-3 font-medium text-slate-600 text-right">Valor Serviço</th>
                      <th className="pb-3 font-medium text-slate-600 text-right">Você Recebe</th>
                      <th className="pb-3 font-medium text-slate-600 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions
                      .sort((a, b) => new Date(b.completed_at || b.date) - new Date(a.completed_at || a.date))
                      .map((tx) => {
                        const statusConfig = FINANCIAL_STATUS_CONFIG[tx.financial_status] || { label: tx.financial_status || 'N/A', color: 'bg-gray-100 text-gray-700' };
                        const price = tx.price || tx.total_price || 0;
                        const providerAmount = tx.provider_amount || (price * providerNetRatio);
                        return (
                          <tr key={tx.id} className="hover:bg-slate-50">
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                {formatDate(tx.completed_at || tx.date)}
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                {tx.client_name || 'Cliente'}
                              </div>
                            </td>
                            <td className="py-3">
                              {formatServiceType(tx.service_type)}
                            </td>
                            <td className="py-3 text-right text-slate-500">
                              {formatCurrency(price)}
                            </td>
                            <td className="py-3 text-right font-medium text-green-600">
                              {formatCurrency(providerAmount)}
                            </td>
                            <td className="py-3 text-center">
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <p className="text-xs text-slate-400 mt-3 text-center">
                  * Comissão da plataforma: {platformFeePercent.toFixed(platformFeePercent % 1 === 0 ? 0 : 1)}% do valor do serviço
                </p>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum serviço concluído ainda</p>
                <p className="text-sm">Seus serviços aparecerão aqui após serem finalizados</p>
              </div>
            )}
          </Card>
        </div>
      </div>
  );
}
